import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { connect } from 'react-redux';
import {
  Dropdown,
  Grid,
  Menu,
  Loader,
  Table,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableHeaderCell,
  Dimmer
} from 'semantic-ui-react';

import CustomDatePicker from '../../../components/CustomDatePicker';
import AppMaskedInput from '../../../components/MaskedInput';
import { DATE_INPUT_MASK, numberToMoneyString } from '../../../services/masks';
import { FieldLabel } from '../../../styles';
import InvoiceDetailModal from './Details';
import { iRootDispatch, iRootState } from '../../../store';
import { AvailableBrokersAndEstatesState } from '../../../store/types/temp-types';
import { CustoDropDownItemProps } from '../../../components/types';
import IconButton from '../../../components/IconButton';
import { InvoicesStatusList, translateInvoiceStatus } from '../utils/AlfaSeguros';
import {
  getInvoices,
  getInvoicesBillet
} from '../../../services/fire-insurance-invoices';
import ProLaboreDetailModal from './ProLaboreDetails';
import { useDropDownUsers } from '../../../services/broker';
import { UsersToCustomDropDownOptions } from '../../../util';
import { UserState } from '../../../types/UserState';
import TablePaginationMenu from '../../../components/TablePaginationMenu';
import { getFiles, getFilesLinkAxios } from '../../../services/file';
import Button from '../../../components/Button';
import { warningNotification } from '../../../services/notification';

const ClickableTableCell = styled(TableCell)`
  cursor: pointer;
  position: relative;
`;

const ClickableTableRow = styled(TableRow)`
  :hover {
    background-color: #f0f0f0;
  }
`;

const FireInsuranceInvoices = (props: {
  availableBrokersAndEstates: AvailableBrokersAndEstatesState;
  user: UserState;
}) => {
  const ALFA_ID = '6101f0f8-ae05-452c-94a3-861c8a8004c4';
  const YELUM_ID = '9ebc4304-201d-4cf2-9c8a-6d8cc90ec968';
  const { availableBrokersAndEstates, user } = props;
  const estatesOptions: CustoDropDownItemProps[] = availableBrokersAndEstates.estates;
  const insurerOptions: CustoDropDownItemProps[] =
    availableBrokersAndEstates.insurers.filter(insurer =>
      insurer.value.availability.includes('FIRE_INSURANCE')
    );

  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [isDownloadBillet, setIsDownloadBillet] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [status, setStatus] = useState<string>('');
  const [start, setStart] = useState<Date>();
  const [end, setEnd] = useState<Date>();
  const [selectedEstate, setSelectedEstate] = useState<any>();
  const [selectedInsurer, setSelectedInsurer] = useState<any>();
  const [selectedAccountManager, setSelectedAccountManager] = useState<any>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openProLaboreModal, setOpenProLaboreModal] = useState<boolean>(false);
  const [currentInvoice, setCurrentInvoice] = useState<any>(null);
  const [invoicesData, setInvoicesData] = useState<any[]>([]);
  const [count, setCount] = useState<number>(0);

  const { data: usersData, isLoading: isAccountManagersLoading } = useDropDownUsers(
    user?.broker?.id
  );

  const accountManagerOptions = React.useMemo(() => {
    if (!usersData) return [];
    return UsersToCustomDropDownOptions(
      false,
      usersData.map((user: any) => ({
        id: user.id,
        name: user.name
      }))
    );
  }, [usersData]);

  const getData = async () => {
    setIsLoadingData(true);

    const filter = {
      insurerId: selectedInsurer?.id || undefined,
      estateId: selectedEstate?.id || undefined,
      accountManager: selectedAccountManager as string,
      start: start ? moment(start).format('YYYY-MM-DD') : undefined,
      end: end ? moment(end).format('YYYY-MM-DD') : undefined,
      status: status || undefined,
      page: currentPage,
      isRv: true
    };

    const invoices = await getInvoices(filter);

    setInvoicesData(invoices?.data);
    setCount(invoices?.quantity);
    setIsLoadingData(false);
  };

  const handleDownloadBillet = async (invoice: any) => {
    setIsDownloadBillet(true);

    await getInvoicesBillet({
      estateId: invoice.estate.id,
      insurerId: invoice.insurer.id,
      contractCode: invoice.contractCode,
      emissionCode: invoice.emissionCode
    });

    setIsDownloadBillet(false);
  };

  async function handleDownloadSegimobFile(invoiceId: string, name: string) {
    setIsDownloadBillet(true);

    try {
      const data = await getFiles({
        tableName: 'fire_insurance_invoices',
        registerId: invoiceId,
        name
      });

      const url = data.urls[0].link;
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.setAttribute('download', data.urls[0].key);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      warningNotification("Arquivo não encontrado", "O Arquivo ainda não foi fornecido pela Seguradora, tente novamente mais tarde.  Se o problema persistir, entre em contato com seu Gestor de Contas.")
    } finally {
      setIsDownloadBillet(false);
    }
  }

  const handleOpenProLaboreDetailsModal = async (invoice: any) => {
    setCurrentInvoice(invoice);
    setOpenProLaboreModal(true);
  };

  const handleCloseProLaboreDetailsModal = () => {
    setCurrentInvoice(null);
    setOpenProLaboreModal(false);
  };

  const handleCloseModal = () => {
    setCurrentInvoice(null);
    setOpenModal(false);
  };

  const renderDetailModal = (invoice: any) => {
    setCurrentInvoice(invoice);
    setOpenModal(true);
  };

  useEffect(() => {
    getData();
  }, [currentPage]);

  return (
    <>
      {isDownloadBillet ? (
        <Dimmer active inverted page>
          <Loader indeterminate>Baixando arquivo...</Loader>
        </Dimmer>
      ) : null}

      <Grid columns={'equal'}>
        <Grid.Row>
          {!user.role.includes('ESTATE') && (
            <Grid.Column>
              <FieldLabel>Imobiliária</FieldLabel>
              <Dropdown
                fluid
                search
                clearable
                selection
                name={'estate'}
                placeholder={'Selecionar'}
                value={selectedEstate}
                options={estatesOptions || []}
                onChange={(e, { value }) => setSelectedEstate(value)}
                defaultValue={estatesOptions.length === 1 ? estatesOptions[0].value : ''}
                disabled={estatesOptions.length === 1 || false}
              />
            </Grid.Column>
          )}

          <Grid.Column>
            <FieldLabel>Seguradora</FieldLabel>
            <Dropdown
              fluid
              search
              clearable
              selection
              name={'insurer'}
              placeholder={'Selecionar'}
              value={selectedInsurer}
              options={insurerOptions || []}
              onChange={(e, { value }) => setSelectedInsurer(value)}
              defaultValue={insurerOptions.length === 1 ? insurerOptions[0].value : ''}
              disabled={insurerOptions.length === 1 || false}
            />
          </Grid.Column>

          {!user.role.includes('ESTATE') && (
            <Grid.Column>
              <FieldLabel>Gestor de contas</FieldLabel>
              <Dropdown
                fluid
                search
                clearable
                selection
                name={'accountManager'}
                placeholder={'Selecionar'}
                value={selectedAccountManager}
                options={accountManagerOptions || []}
                onChange={(e, { value }) => setSelectedAccountManager(value)}
                defaultValue={
                  accountManagerOptions.length === 1 ? accountManagerOptions[0].value : ''
                }
                disabled={accountManagerOptions.length === 1 || false}
              />
            </Grid.Column>
          )}

          <Grid.Column>
            <FieldLabel>Status</FieldLabel>
            <Dropdown
              fluid
              search
              clearable
              selection
              placeholder={'Status'}
              options={InvoicesStatusList}
              onChange={(e, { value }) => setStatus(value as string)}
              value={status}
            />
          </Grid.Column>

          <Grid.Column>
            <FieldLabel>Data de Início</FieldLabel>
            <CustomDatePicker
              fluid
              selected={start}
              maxDate={end}
              type={'date'}
              placeholder={'Data de início'}
              onChange={(date: Date) => setStart(date)}
              customInput={<AppMaskedInput mask={DATE_INPUT_MASK} />}
              dateFormat={'dd/MM/yyyy'}
            />
          </Grid.Column>

          <Grid.Column>
            <FieldLabel>Fim</FieldLabel>
            <CustomDatePicker
              fluid
              selected={end}
              minDate={start}
              type={'date'}
              placeholder={'Data de Fim'}
              onChange={(date: Date) => setEnd(date)}
              customInput={<AppMaskedInput mask={DATE_INPUT_MASK} />}
              dateFormat={'dd/MM/yyyy'}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column textAlign={'right'}>
            <Button
              disabled={isLoadingData}
              loading={isLoadingData}
              onClick={() => {
                setCurrentPage(1);
                getData();
              }}
            >
              Pesquisar
            </Button>

            <Button
              light
              disabled={isLoadingData}
              loading={isLoadingData}
              onClick={() => {
                setStatus('');
                setStart(undefined);
                setEnd(undefined);
                setSelectedEstate('');
                setSelectedAccountManager('');
                setSelectedInsurer('');
              }}
            >
              Limpar
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Table singleLine>
        <TableHeader fullWidth>
          <TableRow>
            <TableHeaderCell textAlign={'center'}>Gerado em</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Data de vencimento</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Data de pagamento</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Imobiliária</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Seguradora</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Valor total</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Gestor de contas</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Status</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Ações</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoadingData ? (
            <TableRow>
              <TableCell textAlign={'center'} colSpan={10}>
                <Loader active inline />
              </TableCell>
            </TableRow>
          ) : (
            <>
              {invoicesData.length ? (
                invoicesData.map((invoice: any) => {
                  const [date, hour] =
                    invoice.createdAt &&
                    moment(invoice.createdAt).format('DD/MM/YYYY HH:mm').split(' ');

                  return (
                    <ClickableTableRow key={invoice.id}>
                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {date}
                        <br />
                        {hour}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {moment(invoice.dueDate).format('DD/MM/YYYY')}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {invoice.paymentDate ? moment(invoice.paymentDate).format('DD/MM/YYYY') : '-'}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {invoice.estate.name}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {invoice.insurer.name}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {numberToMoneyString(Number(invoice.amount))}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {invoice.estate?.user?.name}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {translateInvoiceStatus(invoice.status)}
                      </ClickableTableCell>

                      <TableCell textAlign={'center'}>
                        <Menu compact>
                          <Menu.Item>
                            <Dropdown
                              trigger={<IconButton name="ellipsis vertical" />}
                              icon={null}
                              direction="left"
                            >
                              <Dropdown.Menu style={{ zIndex: 1000 }}>
                                <Dropdown.Item
                                  icon="download"
                                  text={'Baixar boleto'}
                                  onClick={() => {
                                    if (invoice.insurerId === ALFA_ID) {
                                      handleDownloadBillet(invoice);
                                    } else {
                                      handleDownloadSegimobFile(
                                        invoice.id,
                                        invoice.insurerId === YELUM_ID ? 'BOLETO' : 'INVOICE_BILL',
                                      );
                                    }
                                  }}
                                />

                                <Dropdown.Item
                                  icon="info circle"
                                  text={'Baixar faturas fechadas'}
                                  onClick={() =>

                                    handleDownloadSegimobFile(
                                      invoice.id,
                                      'INVOICE_REPORT'
                                    )
                                  }
                                />
                              </Dropdown.Menu>
                            </Dropdown>
                          </Menu.Item>
                        </Menu>
                      </TableCell>
                    </ClickableTableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={10} textAlign={'center'}>
                    Nenhum registro encontrado
                  </TableCell>
                </TableRow>
              )}
            </>
          )}
        </TableBody>

        <Table.Footer fullWidth>
          <TableRow>
            <TableHeaderCell colSpan={10} textAlign={'right'}>
              {!isLoadingData && (
                <TablePaginationMenu
                  currentPage={currentPage}
                  dataCount={count}
                  setCurrentPage={setCurrentPage}
                />
              )}
            </TableHeaderCell>
          </TableRow>
        </Table.Footer>
      </Table>

      {openModal && (
        <InvoiceDetailModal
          open={openModal}
          onClose={handleCloseModal}
          invoice={currentInvoice}
          key={'detailPolicy'}
        />
      )}

      {openProLaboreModal && (
        <ProLaboreDetailModal
          open={openProLaboreModal}
          onClose={handleCloseProLaboreDetailsModal}
          contributionNumber={currentInvoice.contributionNumber}
          key={'proLaboreDetail'}
        />
      )}
    </>
  );
};

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

export default connect(mapState, mapDispatch)(FireInsuranceInvoices);
